import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid'

import Debounce from '@scripts/utilities/debounce.js';
import { ASYNC_STATUS } from '@scripts/utilities/redux-helpers.js';
import RestClient from '@scripts/utilities/rest-client.js';

import { syncPortfolioToDashboard, deletePortfolioAssets, deletePortfolioAssetsByIds, setSelectedPortfolio } from './portfolio.js';
import { getUser } from './user.js';

const VERSION = 'v1';

const initialState = {
  status: ASYNC_STATUS.IDLE,
  // A list of the asset table columns to show/hide.
  assetTableColumns: [],
  // The sorting preference for the asset table.
  assetTableSort: null,
  // A list of the NFT Collection table columns to show/hide.
  collectionTableColumns: [],
  // The sorting preference for the NFT Collection table.
  collectionTableSort: null,
  // A list of the portfolio table columns to show/hide.
  portfoliosTableColumns: [],
  // The sorting preference for the portfolio table
  PortfoliosTableSort: null,
  // Preferences around the personalised dashboard.
  dashboard: {
    assets: [],
    blockchains: [],
    categories: [],
    portfolios: [],
    goals: []
  }
};


var fetchPreferences = createAsyncThunk('userPreferences/fetch', function() {
  return RestClient.get(`/wp-json/sbm/${VERSION}/user/preferences/`);
});

/**
 * Save the user's preference changes to the server. We de-bounce the script
 * here in case several updates are made in quick succession.
 */
var persistUserPreferences = Debounce(function(state) {
    var user = getUser(state);

    if (user && user.activeMember) {
      var preferences = getUserPreferences(state);
      RestClient.post(`/wp-json/sbm/${VERSION}/user/preferences/`, preferences);
    }
}, 1000);


const userPreferenceSlice = createSlice({
    name: 'userPreferences',
    initialState: initialState,
    reducers: {
      /**
       * A special-case to bypass the fetch step. Only to be used for
       * non-logged in users.
       */
      bypassFetch: function(state, action) {
        state.status = ASYNC_STATUS.COMPLETE;
      },
      // Hide a column from the asset table.
      hideAssetTableColumn: function(state, action) {
        const columnName = action.payload;

        // If it's in the array as shown, remove it.
        var index = state.assetTableColumns.indexOf(columnName + '+');
        if (index > -1) {
          state.assetTableColumns.splice(index, 1);
        }

        // If it's not already in the array as hidden, add it.
        if (state.assetTableColumns.indexOf(columnName + '-') === -1) {
          state.assetTableColumns.push(columnName + '-');
        }
      },
      // Show a column on the asset table.
      showAssetTableColumn: function(state, action) {
        const columnName = action.payload;

        // If it's in the array as hidden, remove it.
        var index = state.assetTableColumns.indexOf(columnName + '-');
        if (index > -1) {
          state.assetTableColumns.splice(index, 1);
        }

        // If it's not already in the array as shown, add it.
        if (state.assetTableColumns.indexOf(columnName + '+') === -1) {
          state.assetTableColumns.push(columnName + '+');
        }
      },
      setAssetTableSort: function(state, action) {
        var sortColumn = action.payload;

        // Toggle sort of column if we're already sorted by the column.
        if (state.assetTableSort == sortColumn + '+') {
          state.assetTableSort = sortColumn + '-';
        } else {
          if (state.assetTableSort == sortColumn + '-') {
            state.assetTableSort = sortColumn + '+';
          } else {
            // Sort by the column, ascending.
            state.assetTableSort = sortColumn + '+';
          }
        }
      },
      /**
       * Hide a column from the portfolio table.
       */
      hidePortfolioTableColumn: function(state, action) {
        const columnName = action.payload;

        // If it's in the array as shown, remove it.
        var index = state.portfoliosTableColumns.indexOf(columnName + '+');
        if (index > -1) {
          state.portfoliosTableColumns.splice(index, 1);
        }

        // If it's not already in the array as hidden, add it.
        if (state.portfoliosTableColumns.indexOf(columnName + '-') === -1) {
          state.portfoliosTableColumns.push(columnName + '-');
        }
      },
      // Show a column on the portfolio table.
      showPortfolioTableColumn: function(state, action) {
        const columnName = action.payload;

        // If it's in the array as hidden, remove it.
        var index = state.portfoliosTableColumns.indexOf(columnName + '-');
        if (index > -1) {
          state.portfoliosTableColumns.splice(index, 1);
        }

        // If it's not already in the array as shown, add it.
        if (state.portfoliosTableColumns.indexOf(columnName + '+') === -1) {
          state.portfoliosTableColumns.push(columnName + '+');
        }
      },
      setPortfoliosTableSort: function(state, action) {
        var sortColumn = action.payload;

        // Toggle sort of column if we're already sorted by the column.
        if (state.PortfoliosTableSort == sortColumn + '+') {
          state.PortfoliosTableSort = sortColumn + '-';
        } else {
          if (state.PortfoliosTableSort == sortColumn + '-') {
            state.PortfoliosTableSort = sortColumn + '+';
          } else {
            // Sort by the column, ascending.
            state.PortfoliosTableSort = sortColumn + '+';
          }
        }
      },
      // Hide a column from the NFT Collection table.
      hideCollectionTableColumn: function(state, action) {
        const columnName = action.payload;

        // If it's in the array as shown, remove it.
        var index = state.collectionTableColumns.indexOf(columnName + '+');
        if (index > -1) {
          state.collectionTableColumns.splice(index, 1);
        }

        // If it's not already in the array as hidden, add it.
        if (state.collectionTableColumns.indexOf(columnName + '-') === -1) {
          state.collectionTableColumns.push(columnName + '-');
        }
      },
      // Show a column on the NFT Collection table.
      showCollectionTableColumn: function(state, action) {
        const columnName = action.payload;

        // If it's in the array as hidden, remove it.
        var index = state.collectionTableColumns.indexOf(columnName + '-');
        if (index > -1) {
          state.collectionTableColumns.splice(index, 1);
        }

        // If it's not already in the array as shown, add it.
        if (state.collectionTableColumns.indexOf(columnName + '+') === -1) {
          state.collectionTableColumns.push(columnName + '+');
        }
      },
      setCollectionTableSort: function(state, action) {
        var sortColumn = action.payload;

        // Toggle sort of column if we're already sorted by the column.
        if (state.collectionTableSort == sortColumn + '+') {
          state.collectionTableSort = sortColumn + '-';
        } else {
          if (state.collectionTableSort == sortColumn + '-') {
            state.collectionTableSort = sortColumn + '+';
          } else {
            // Sort by the column, ascending.
            state.collectionTableSort = sortColumn + '+';
          }
        }
      },
      setDashboardAssets: function(state, action) {
        state.dashboard.assets = action.payload;
      },
      setDashboardBlockchains: function(state, action) {
        state.dashboard.blockchains = action.payload;
      },
      setDashboardCategories: function(state, action) {
        state.dashboard.categories = action.payload;
      },
      setDashboardPortfolio: function(state, action) {
        if(!action.payload.id) {
          state.dashboard.portfolios.push({...action.payload, id: uuid()});
          return;
        }
        const portfolioIndex = state.dashboard.portfolios.findIndex(portfolio => portfolio.id === action.payload.id);
        if(portfolioIndex === -1) {
          return;
        }
        state.dashboard.portfolios[portfolioIndex] = action.payload;
      },
      deleteDashboardPortfolio: function(state, action) {
        if(action.payload) {
          state.dashboard.portfolios = state.dashboard.portfolios.filter(portfolio => portfolio.id !== action.payload);
          // Also remove goals associated with this portfolio
          state.dashboard.goals = state.dashboard.goals.filter(goal => goal.portfolio_id !== action.payload);
        }
      },
      setDashboardGoal: function(state, action) {
        if(!action.payload.id) {
          // Creating new goal - generate UUID and add to array
          const newGoal = {
            ...action.payload,
            id: uuid(),
            created_date: new Date().toISOString(),
            status: 'active',
          };
          state.dashboard.goals.push(newGoal);
          return;
        }
        // Updating existing goal
        const goalIndex = state.dashboard.goals.findIndex(goal => goal.id === action.payload.id);
        if(goalIndex !== -1) {
          state.dashboard.goals[goalIndex] = {
            ...state.dashboard.goals[goalIndex],
            ...action.payload
          };
        }
      },
      deleteDashboardGoal: function(state, action) {
        if(action.payload) {
          state.dashboard.goals = state.dashboard.goals.filter(goal => goal.id !== action.payload);
        }
      },
      completeGoal: function(state, action) {
        const goalIndex = state.dashboard.goals.findIndex(goal => goal.id === action.payload);
        if(goalIndex !== -1) {
          state.dashboard.goals[goalIndex].status = 'completed';
          state.dashboard.goals[goalIndex].completed_date = new Date().toISOString();
        }
      }
    },
    extraReducers: function(builder) {
      builder
        .addCase(fetchPreferences.pending, function(state) {
          state.status = ASYNC_STATUS.INPROGRESS;
        })
        .addCase(fetchPreferences.fulfilled, function(state, action) {
          state.assetTableColumns = action.payload.assetTableColumns;
          state.assetTableSort = action.payload.assetTableSort;
          state.portfoliosTableColumns = action.payload.portfoliosTableColumns;
          state.PortfoliosTableSort = action.payload.PortfoliosTableSort;
          state.collectionTableColumns = action.payload.collectionTableColumns;
          state.collectionTableSort = action.payload.collectionTableSort;
          state.dashboard = action.payload.dashboard;
          state.status = ASYNC_STATUS.COMPLETE;
        });
    }
});


// Helper function for retrieving the user preferences from the store
export function getUserPreferences(state) {
  return state.userPreferences;
}


// Action creators
export var fetch = function() {
  return function(dispatch, getState) {
    var state = getState();
    var user = getUser(state);

    if (user && user.activeMember) {
      var userPreferences = getUserPreferences(state);

      if (userPreferences.status == ASYNC_STATUS.IDLE) {
        dispatch(fetchPreferences());
      }
    } else {
      dispatch(userPreferenceSlice.actions.bypassFetch());
    }
  };
};

export const hideAssetTableColumn = function(column) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.hideAssetTableColumn(column));

    persistUserPreferences(getState());
  };
};
export const showAssetTableColumn = function(column) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.showAssetTableColumn(column));

    persistUserPreferences(getState());
  };
};
export const setAssetTableSort = function(sortOption) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.setAssetTableSort(sortOption));

    persistUserPreferences(getState());
  };
};

export var hidePortfolioTableColumn = function(column) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.hidePortfolioTableColumn(column));

    persistUserPreferences(getState());
  };
};
export var showPortfolioTableColumn = function(column) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.showPortfolioTableColumn(column));

    persistUserPreferences(getState());
  };
};
export var setPortfoliosTableSort = function(sortOption) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.setPortfoliosTableSort(sortOption));

    persistUserPreferences(getState());
  };
};

export var hideCollectionTableColumn = function(column) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.hideCollectionTableColumn(column));

    persistUserPreferences(getState());
  };
};
export var showCollectionTableColumn = function(column) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.showCollectionTableColumn(column));

    persistUserPreferences(getState());
  };
};
export var setCollectionTableSort = function(sortOption) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.setCollectionTableSort(sortOption));

    persistUserPreferences(getState());
  };
};
export var setDashboardAssets = function(assets) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.setDashboardAssets(assets));

    persistUserPreferences(getState());
    
    dispatch(syncPortfolioToDashboard(assets));
  };
};
export var setDashboardBlockchains = function(blockchains) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.setDashboardBlockchains(blockchains));

    persistUserPreferences(getState());
  };
};
export var setDashboardCategories = function(categories) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.setDashboardCategories(categories));

    persistUserPreferences(getState());
  };
};

export const setDashboardPortfolio = function(portfolio) {
  return function(dispatch, getState) {

    const oldPortfolio = getState().userPreferences.dashboard.portfolios.find(p => p.id === portfolio.id);
    dispatch(userPreferenceSlice.actions.setDashboardPortfolio(portfolio));

    persistUserPreferences(getState());

    // Sync created portfolio id
    if(!portfolio.id) {
      const state = getState().userPreferences;
      portfolio.id = state.dashboard.portfolios[state.dashboard.portfolios.length -1].id
    } else if(oldPortfolio.assets && portfolio.assets) {
      // Remove old assets quantity if necessary
      const intersection = oldPortfolio.assets.filter(assetId => !portfolio.assets.includes(assetId));
      if(intersection) {
        dispatch(deletePortfolioAssetsByIds(intersection, oldPortfolio.id));
      }
    }
    dispatch(setSelectedPortfolio(portfolio));    
  };
};

export var deleteDashboardPortfolio = function(portfolioId) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.deleteDashboardPortfolio(portfolioId));

    persistUserPreferences(getState());

    dispatch(deletePortfolioAssets(portfolioId));
    dispatch(setSelectedPortfolio(undefined));
  };
};

export const setDashboardGoal = function(goal) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.setDashboardGoal(goal));
    persistUserPreferences(getState());
  };
};

export const deleteDashboardGoal = function(goalId) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.deleteDashboardGoal(goalId));
    persistUserPreferences(getState());
  };
};

export const completeGoal = function(goalId) {
  return function(dispatch, getState) {
    dispatch(userPreferenceSlice.actions.completeGoal(goalId));
    persistUserPreferences(getState());
  };
};

export default userPreferenceSlice.reducer;

